import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import Skeleton from '@mui/material/Skeleton';
import Divider from '@mui/material/Divider';
import AddIcon from '@mui/icons-material/Add';
import ExploreIcon from '@mui/icons-material/Explore';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SendIcon from '@mui/icons-material/Send';
import BadgeIcon from '@mui/icons-material/Badge';
import { apiGet } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { PURPLE_AVATAR } from '../theme/tokens';

interface DashboardStats {
    badgesCreated: number;
    badgesEarned:  number;
    badgesIssued:  number;
}

interface RecentEarned {
    id:          string;
    badgeName:   string;
    badgeImage:  string | null;
    earnedAt:    string;
}

interface RecentIssued {
    id:             string;
    badgeName:      string;
    badgeImage:     string | null;
    recipientEmail: string;
    issuedAt:       string;
}

interface DashboardData {
    stats:         DashboardStats;
    recentEarned:  RecentEarned[];
    recentIssued:  RecentIssued[];
}

function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function StatCard({ icon, value, label, loading }: {
    icon:    React.ReactNode;
    value:   number;
    label:   string;
    loading: boolean;
}) {
    return (
        <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
                {icon}
                {loading ? (
                    <Skeleton variant="text" width={60} sx={{ mx: 'auto', mt: 1 }} height={48} />
                ) : (
                    <Typography variant="h4" fontWeight={700} mt={1}>{value.toLocaleString()}</Typography>
                )}
                <Typography variant="body2" color="text.secondary">{label}</Typography>
            </CardContent>
        </Card>
    );
}

function QuickAction({ icon, label, to }: { icon: React.ReactNode; label: string; to: string }) {
    const navigate = useNavigate();
    return (
        <Card>
            <CardActionArea onClick={() => navigate(to)} sx={{ py: 3, textAlign: 'center' }}>
                <Box sx={{ color: 'accent.main', mb: 1 }}>{icon}</Box>
                <Typography variant="body1" fontWeight={600}>{label}</Typography>
            </CardActionArea>
        </Card>
    );
}

export default function HomeDashboardPage() {
    const { user } = useAuth();
    const [data, setData]       = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        void apiGet<DashboardData>('/api/dashboard/summary').then(res => {
            if (res.success && res.data) setData(res.data);
            setLoading(false);
        });
    }, []);

    const stats = data?.stats;

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" fontWeight={700} mb={1}>
                Welcome{user?.firstName ? `, ${user.firstName}` : ''}
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={4}>
                Here's a snapshot of your activity.
            </Typography>

            {/* Stat cards */}
            <Grid container spacing={3} mb={5}>
                <Grid item xs={12} md={4}>
                    <StatCard
                        icon={<BadgeIcon sx={{ fontSize: 40, color: 'accent.main' }} />}
                        value={stats?.badgesCreated ?? 0}
                        label="Badges Created"
                        loading={loading}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <StatCard
                        icon={<EmojiEventsIcon sx={{ fontSize: 40, color: 'accent.main' }} />}
                        value={stats?.badgesEarned ?? 0}
                        label="Badges Earned"
                        loading={loading}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <StatCard
                        icon={<SendIcon sx={{ fontSize: 40, color: 'accent.main' }} />}
                        value={stats?.badgesIssued ?? 0}
                        label="Badges Issued"
                        loading={loading}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={4}>
                {/* Recent activity */}
                <Grid item xs={12} md={8}>
                    <Typography variant="h6" fontWeight={600} mb={2}>Recent Activity</Typography>

                    {loading ? (
                        Array.from({ length: 4 }).map((_, i) => (
                            <Box key={i} display="flex" alignItems="center" gap={2} mb={2}>
                                <Skeleton variant="circular" width={40} height={40} />
                                <Box flexGrow={1}>
                                    <Skeleton variant="text" width="60%" />
                                    <Skeleton variant="text" width="40%" />
                                </Box>
                            </Box>
                        ))
                    ) : (!data?.recentEarned.length && !data?.recentIssued.length) ? (
                        <Typography color="text.secondary">No activity yet. Start by discovering badges below!</Typography>
                    ) : (
                        <Card variant="outlined">
                            <List disablePadding>
                                {data.recentEarned.map((item, idx) => (
                                    <>
                                        {idx > 0 && <Divider component="li" key={`d-e-${item.id}`} />}
                                        <ListItem key={item.id}>
                                            <ListItemAvatar>
                                                <Avatar
                                                    src={item.badgeImage ?? undefined}
                                                    sx={{ bgcolor: PURPLE_AVATAR }}
                                                >
                                                    {!item.badgeImage && <WorkspacePremiumIcon fontSize="small" />}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={<>You earned <strong>{item.badgeName}</strong></>}
                                                secondary={formatDate(item.earnedAt)}
                                            />
                                        </ListItem>
                                    </>
                                ))}
                                {data.recentIssued.map((item, idx) => (
                                    <>
                                        {(idx > 0 || data.recentEarned.length > 0) && <Divider component="li" key={`d-i-${item.id}`} />}
                                        <ListItem key={item.id}>
                                            <ListItemAvatar>
                                                <Avatar
                                                    src={item.badgeImage ?? undefined}
                                                    sx={{ bgcolor: '#5c35c9' }}
                                                >
                                                    {!item.badgeImage && <SendIcon fontSize="small" />}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={<>Issued <strong>{item.badgeName}</strong> to {item.recipientEmail}</>}
                                                secondary={formatDate(item.issuedAt)}
                                            />
                                        </ListItem>
                                    </>
                                ))}
                            </List>
                        </Card>
                    )}
                </Grid>

                {/* Quick actions */}
                <Grid item xs={12} md={4}>
                    <Typography variant="h6" fontWeight={600} mb={2}>Quick Actions</Typography>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <QuickAction icon={<AddIcon sx={{ fontSize: 32 }} />}          label="Create a Badge"    to="/badges/create" />
                        <QuickAction icon={<ExploreIcon sx={{ fontSize: 32 }} />}       label="Discover Badges"  to="/available-badges" />
                        <QuickAction icon={<WorkspacePremiumIcon sx={{ fontSize: 32 }} />} label="My Portfolio"  to="/portfolio" />
                    </Box>
                </Grid>
            </Grid>
        </Container>
    );
}
